import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, EMPTY, from, Observable, of, throwError } from 'rxjs';
import { Client, Conversation, ConversationUpdateReason, Message, Participant } from '@twilio/conversations';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { catchError, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AppStateService } from '../../store/app-state.service';
import { TWILIO_USER_TOKEN_KEY } from '@shared/constants';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { CONVERSATION_CLIENT_EVENT_NAMES } from '@modules/messenger/messenger.constants';
import { ConversationsCollectionService, MessagesCollectionService, ParticipantsCollectionService } from '../../store/store-services';
import { ConversationStateModel, MessageStateModel, ParticipantStateModel } from '../../store/store-models';
import { ResponseModel } from '@modules/auth/_models/response.model';

const TWILIO_URL = `${environment.twilioUrl}`;
const API_USERS_URL = `${environment.apiUrl}`;

@Injectable({
  providedIn: 'root',
})
export class MessengerService {
  private readonly chatClientSubject: BehaviorSubject<Client | null> = new BehaviorSubject<Client>(null);
  private readonly chatClient$: Observable<Client> = this.chatClientSubject.pipe(
    filter(it => Boolean(it)),
  );
  chatClientReady$: Observable<Client> = this.chatClient$.pipe(
    switchMap((chatClient) => from(new Promise<Client>((rs) => {
      chatClient.once('initialized', () => rs(chatClient));
    }))),
    shareReplay({bufferSize: 1, refCount: false}),
  );

  get chatClient(): Client {
    return this.chatClientSubject.getValue();
  }


  get currentUserId() {
    return this.appStateService.state.currentUser?.id;
  }

  private httpClientWithoutInterceptors: HttpClient = new HttpClient(this.httpBackend);

  constructor(
    private http: HttpClient,
    private appStateService: AppStateService,
    private httpBackend: HttpBackend,
    private conversationsService: ConversationsCollectionService,
    private messagesService: MessagesCollectionService,
    private participantsService: ParticipantsCollectionService,
  ) {
    (window as any).tw = this.chatClientSubject;
  }

  // TODO: if we want to remove messages in future \/
  // https://www.twilio.com/docs/conversations/modifying-objects

  init(twilioJWT: string) {
    console.warn('Twilio init...');
    this.chatClientSubject.next(new Client(twilioJWT));

    if (!environment.production) {
      CONVERSATION_CLIENT_EVENT_NAMES.forEach((eventName) => {
        this.chatClient.on(eventName, (...args) => console.log(`[ChatClient] ${String(eventName)}`, args, this.chatClient));
      });
    }

    this.chatClient.on('tokenAboutToExpire', () => this.updateToken().subscribe());
    this.chatClient.on('tokenExpired', () => this.updateToken().subscribe());

    this.chatClient.on('conversationAdded', conversation => {
      this.addConversation(conversation);
    });

    this.chatClient.on('conversationUpdated', conversationUpdate => {
      this.updateConversation(conversationUpdate);
    });

    this.chatClient.on('conversationRemoved', conversation => {
      this.removeConversation(conversation);
    });

    this.chatClient.on('messageAdded', message => {
      this.addMessages([message]);
    });

    this.chatClient.on('messageRemoved', message => {
      this.removeMessages([message]);
    });
  }

  addConversation(conversation: Conversation) {
    if (this.conversationsService.entityMap.get(conversation.uniqueName)) {
      return;
    }

    this.conversationsService.entityMap.set(conversation.uniqueName, conversation);

    this.conversationsService.upsertOneInCache(this.mapConversationToState(conversation));

    Promise.all([
      conversation.getMessagesCount(),
      conversation.getUnreadMessagesCount()
    ]).then(([messagesCount, unreadMessagesCount]) => {
      this.conversationsService.upsertOneInCache({
        sid: conversation.sid,
        uniqueName: conversation.uniqueName,
        messagesCount,
        unreadMessagesCount,
      });

      if (messagesCount) {
        conversation.getMessages(10).then(messagesPaginator => {
          this.addMessages(messagesPaginator.items);
        });
      }
    });

    conversation.getParticipants().then((participants) => {
      this.addParticipants(participants);
    });
  }

  updateConversation({conversation, updateReasons}: { conversation: Conversation; updateReasons: ConversationUpdateReason[]; }) {
    this.conversationsService.upsertOneInCache(this.mapConversationToState(conversation));

    Promise.all([
      conversation.getMessagesCount(),
      conversation.getUnreadMessagesCount()
    ]).then(([messagesCount, unreadMessagesCount]) => {
      this.conversationsService.upsertOneInCache({
        sid: conversation.sid,
        uniqueName: conversation.uniqueName,
        messagesCount,
        unreadMessagesCount,
      });
    });

    conversation.getParticipants().then((participants) => {
      this.addParticipants(participants);
    });
  }

  removeConversation(conversation: Conversation) {
    this.conversationsService.entityMap.remove(conversation.uniqueName);
    this.conversationsService.removeOneFromCache(conversation.uniqueName);
    // TODO async remove all related messages from the entity cache and it`s entityMap
    // TODO async remove all related participants from the entity cache and it`s entityMap
  }

  addMessages(messages: Message[]) {
    if (!messages?.length) {
      return;
    }
    this.messagesService.upsertManyInCache(messages.map(it => {
      this.messagesService.entityMap.set(it.sid, it);
      return this.mapMessageToState(it);
    }));

    // Get body of the last message and update it`s 'conversation.lastMessageBody' ONLY,
    // because 'lastMessageIndex' and 'lastMessageDateCreated' are updated in 'updateConversation' method
    const {conversation: {sid, uniqueName}, body: lastMessageBody} = messages[messages.length - 1];
    this.conversationsService.upsertOneInCache({
      sid, uniqueName, lastMessageBody,
    });
  }

  removeMessages(messages: Message[]) {
    if (!messages?.length) {
      return;
    }
    this.messagesService.removeManyFromCache(messages.map(it => {
      this.messagesService.entityMap.remove(it.sid);
      return it.sid;
    }));
  }

  addParticipants(participants: Participant[]) {
    this.participantsService.upsertManyInCache(participants.map(it => {
      this.participantsService.entityMap.set(it.sid, it);
      return this.mapParticipantToState(it);
    }));
  }

  removeParticipants(participants: Participant[]) {
    this.participantsService.removeManyFromCache(participants.map(it => {
      this.participantsService.entityMap.remove(it.sid);
      return it.sid;
    }));
  }

  getConversation(userId): Promise<Conversation> {
    console.log('getting conversation...');
    const uniqueName = this.getOne2OneConversationUniqueName(userId);

    return (this.chatClient ? Promise.resolve(this.chatClient) : this.chatClientReady$.pipe(take(1)).toPromise())
      .then(it => it.getConversationByUniqueName(uniqueName))
      .then((it) => {
        console.log('conversation found', it);
        return it;
      })
      .catch((it) => {
        console.warn('conversation not found');
        throw it;
      });
  }

  createConversation(userId, friendlyName?): Promise<Conversation> {
    console.log('creating conversation...');
    const uniqueName = this.getOne2OneConversationUniqueName(userId);

    return (this.chatClient ? Promise.resolve(this.chatClient) : this.chatClientReady$.pipe(take(1)).toPromise())
      .then(it =>
        this.chatClient.createConversation({
          uniqueName,
          friendlyName,
        }).then(conversation =>
          Promise.all(
            [...new Set([userId, this.currentUserId])].map(uniqueUserId =>
              conversation.add(uniqueUserId, {userName: `U_${uniqueUserId}`}))
          ).then(() => conversation)
        )
      );
  }

  deleteConversation(userId: string) {
    console.log('deleting conversation...');
    const uniqueName = this.getOne2OneConversationUniqueName(userId);

    return (this.chatClient ? Promise.resolve(this.chatClient) : this.chatClientReady$.pipe(take(1)).toPromise())
      .then(client => client.getConversationByUniqueName(uniqueName))
      .then((conversation) => conversation?.delete())
      .then((conversation) => {
        console.log('conversation was deleted');
        return conversation;
      })
      .catch((it) => {
        console.warn('conversation not found');
        throw it;
      });
  }

  deleteAllConversation(): Observable<any> {
    console.log('deleting all conversations...');
    return (this.chatClient ? of(this.chatClient) : this.chatClientReady$).pipe(
      switchMap((chatClient) => from(chatClient.getSubscribedConversations())),
      switchMap(({items: conversations}) => from(
        conversations?.length ? 
          Promise.all(conversations?.map((conversation) => conversation?.delete())) : 
          Promise.resolve(null)
      )),
      tap(() => console.log('deleting all conversations...')),
      catchError((error) => {
        console.error('error while deleting conversations', error);
        throw error;
      }),
    );
  }
  

  isMessageMine({author: messageAuthorIdentity}: Message) {
    const currentUserIdentity = this.chatClient?.user?.identity;
    return currentUserIdentity === messageAuthorIdentity;
  }

  getTwilioToken(identity: string): Observable<string> {
    const persistentTwilioToken = localStorage.getItem(TWILIO_USER_TOKEN_KEY);
    return this.isJWTValid(persistentTwilioToken, identity)
      ? of(this.setTwilioToken(persistentTwilioToken))
      : this.fetchTwilioTokenAndSave(identity);
  }

  updateToken() {
    return this.fetchTwilioTokenAndSave(this.currentUserId).pipe(
      switchMap(updatedToken => this.chatClient.updateToken(updatedToken))
    );
  }

  async shutDown() {
    return this.chatClient?.shutdown();
  }

  getOne2OneConversationUniqueName(participantUserId): string {
    if (!this.currentUserId) {
      throw new Error(`No currentUserId '${this.currentUserId}'`);
    }
    const userIds = [this.currentUserId, participantUserId].sort();
    if (userIds.length !== 2) {
      throw new Error(`Invalid uniqueName params '${userIds}'`);
    }
    return `${userIds.join(':')}`;
  }

  parserOne2OneConversationUniqueName(uniqueName: string): string {
    const userIds = uniqueName.split(':');
    if ((userIds.length !== 2)) {
      throw new Error(`Invalid uniqueName '${uniqueName}'`);
    }
    if (!this.currentUserId || !userIds.includes(this.currentUserId)) {
      throw new Error(`Invalid uniqueName '${uniqueName}'`);
    }
    return userIds.filter(it => it !== this.currentUserId)[0] ?? this.currentUserId;
  }

  fetchTwilioTokenAndSave(identity: string): Observable<string> {
    return this.fetchTwilioToken(identity).pipe(
      tap((jwToken) => {
        localStorage.setItem(TWILIO_USER_TOKEN_KEY, jwToken);
        this.setTwilioToken(jwToken);
      }),
      catchError((err) => {
        console.warn('failed to get twilio token', err);
        return throwError(err);
      }),
    );
  }

  fetchTwilioToken(identity: string): Observable<string> {
    const params = {identity};
    return this.httpClientWithoutInterceptors.get<string>(`${TWILIO_URL}/generate-chat-token`, {params});
  }

  notifyChatUser(recipientUserId: string, message: string): Observable<boolean> {
    return this.http.post<ResponseModel<boolean>>(`${API_USERS_URL}/v1/Notification/notify-chat-user`, {
      recipientUserId, message
    }).pipe(
      map((response) => response.data),
      catchError((err) => {
        console.warn('failed to send notification', err);
        return EMPTY;
      }),
    )
  }

  private setTwilioToken(jwToken: string) {
    this.appStateService.patchState({twilioToken: jwToken});
    return jwToken;
  }

  private isJWTValid(jwt: string, currentIdentity: string): boolean {
    if (!(jwt && currentIdentity)) {
      return false;
    }

    let jwtData: JwtPayload & { grants?: { identity?: string } };
    try {
      jwtData = jwtDecode<JwtPayload>(jwt);
    } catch (e) {
      console.warn('jwtDecode failed', e);
      return false;
    }

    if (currentIdentity !== jwtData.grants?.identity) { // token from another user
      return false;
    }

    const nowTimeStamp = Date.now();
    const expirationTimeStamp = jwtData.exp * 1000;

    return (nowTimeStamp - expirationTimeStamp) > 600; // more than 10 minutes left until expire
  }

  private mapConversationToState(conversation: Conversation): ConversationStateModel {
    const {
      sid, uniqueName, createdBy, dateCreated, dateUpdated, friendlyName, lastReadMessageIndex, lastMessage
    } = conversation;
    return {
      sid, uniqueName, createdBy, friendlyName,
      dateCreated: dateCreated.toISOString(),
      dateUpdated: dateUpdated.toISOString(),
      lastMessageIndex: lastMessage?.index,
      lastMessageDateCreated: lastMessage?.dateCreated?.toISOString(),
      lastReadMessageIndex: lastReadMessageIndex ?? -1,
    };
  }

  private mapMessageToState(message: Message): MessageStateModel {
    const {
      attributes, author, body, conversation, dateCreated, dateUpdated, index, lastUpdatedBy, participantSid, sid, type
    } = message;
    return {
      sid, type, body, author, index, attributes, lastUpdatedBy, participantSid,
      conversationUniqueName: conversation.uniqueName,
      conversationSid: conversation.sid,
      dateCreated: dateCreated.toISOString(),
      dateUpdated: dateUpdated.toISOString(),
    };
  }

  /* Maps instance of Participant from Twilio Conversations to object, implementing ParticipantStateModel interface*/
  private mapParticipantToState(participant: Participant): ParticipantStateModel {
    const {
      sid, identity, type, isTyping, conversation, dateCreated, dateUpdated, lastReadMessageIndex, roleSid,
    } = participant;
    return {
      sid, identity, type, isTyping, roleSid,
      conversationSid: conversation.sid,
      conversationUniqueName: conversation.uniqueName,
      dateCreated: dateCreated.toISOString(),
      dateUpdated: dateUpdated.toISOString(),
      lastReadMessageIndex: lastReadMessageIndex ?? -1,
    };
  }
}
